import type { Recipe } from 'agentic_chat/types';
import type { Clip } from '@goldcast/player-config/src/types/clip-types';
import type { SizeType } from '@goldcast/player-config/src/types/asset-types';
import { getFromCache, CACHE_KEYS } from '../cache';
import { updateClipMetadataWithId, saveClipChanges, trackClipUpdate } from '@/stores/clip';
import { clipVersionStore } from '@/stores/clipVersionStore';
import { VERSION_EDIT_TYPE } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils/constants';
import { brandKit, getSelectedPlatformBrandKit } from '@/stores/brandKit';
import { getBrandKitUrl } from '@/Pages/BrandKit/components/utils';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import type { PlatformBrandKitColors, PlatformBrandKitImage } from '@/Pages/BrandKit/types';

function getBackgroundFromBrandKit(): { backgroundImage?: string; backgroundColor?: string } {
  const usePlatformBrandKit = !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Platform_Brandkit];

  if (usePlatformBrandKit) {
    const activeKit = getSelectedPlatformBrandKit();
    const photographyElements = (activeKit?.photography_elements as PlatformBrandKitImage[] | undefined) ?? [];
    const logos = (activeKit?.logos as PlatformBrandKitImage[] | undefined) ?? [];
    const colors = activeKit?.colors as PlatformBrandKitColors | undefined;

    const backgroundImage = photographyElements[0]?.url ?? logos[0]?.url;
    if (backgroundImage) {
      return { backgroundImage };
    }

    const primaryColor = colors?.primary?.[0];
    if (primaryColor) {
      return { backgroundColor: primaryColor };
    }

    return { backgroundColor: '#000000' };
  }

  const brandKitSnapshot = brandKit.getSnapshot();
  const backgroundImages = brandKitSnapshot.background || [];
  const brandColors = brandKitSnapshot.colors || [];

  if (backgroundImages.length > 0) {
    return { backgroundImage: getBrandKitUrl(backgroundImages[0]) };
  }

  if (brandColors.length > 0) {
    return { backgroundColor: brandColors[0] };
  }

  return { backgroundColor: '#000000' };
}

function getRecommendedOrFirstRecipe(clipId: string): string {
  const recipes = getFromCache<Recipe[]>(CACHE_KEYS.recipes) || [];
  const recommendedRecipeIds = getFromCache<string[] | null>(CACHE_KEYS.recommendedRecipe(clipId));

  if (recipes.length === 0) {
    throw new Error('No recipes available');
  }

  let selectedRecipe: Recipe | undefined;

  if (recommendedRecipeIds && recommendedRecipeIds.length > 0) {
    const firstRecommendedId = recommendedRecipeIds[0];
    selectedRecipe = recipes.find(r => r.id === firstRecommendedId);
  }

  if (!selectedRecipe) {
    selectedRecipe = recipes[0];
  }

  return selectedRecipe.prompt;
}

function validateRecipesAvailable(): void {
  const recipes = getFromCache<Recipe[]>(CACHE_KEYS.recipes) || [];

  if (recipes.length === 0) {
    throw new Error('No recipes available');
  }
}

function applyPlatformOptimization(clipId: string, clipData: Clip, size: SizeType): void {
  const background = getBackgroundFromBrandKit();
  const currentMagicLayout = clipData.asset_metadata.magicLayout;

  updateClipMetadataWithId(clipId, {
    size,
    magicLayout: currentMagicLayout
      ? {
          ...currentMagicLayout,
          ...background
        }
      : {
          textColor: '#FFFFFF',
          backgroundColor: background.backgroundColor || '#000000',
          showSpeakerLabels: false,
          backgroundImage: background.backgroundImage
        }
  });

  clipVersionStore.emitEdit(clipId, VERSION_EDIT_TYPE.AI_QUICK_ACTION);
  saveClipChanges({ clipId });
  trackClipUpdate(clipId, 'PLATFORM_OPTIMIZATION');
}

export async function handleLinkedInOptimization(clipId: string, clipData: Clip): Promise<string> {
  validateRecipesAvailable();
  applyPlatformOptimization(clipId, clipData, 'SQUARE');
  return getRecommendedOrFirstRecipe(clipId);
}

export async function handleYouTubeOptimization(clipId: string, clipData: Clip): Promise<string> {
  validateRecipesAvailable();
  applyPlatformOptimization(clipId, clipData, 'PORTRAIT');
  return getRecommendedOrFirstRecipe(clipId);
}
